import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Button } from "@/design-system/Button/Button";
import {
  RoleOption,
  RoleSelectSheetContent,
} from "@/design-system/RoleSelectPanel/RoleSelectPanel";
import BackButton from "@/design-system/SpecificButtons/BackButton/BackButton";
import { CompareSubsection } from "@/events/compareMixpanel";
import { useGoBack } from "@/hooks/useGoBack";
import { cn } from "@/lib/utils";
import { PlayerRole } from "@/redux/players/playersSlice";
import type { ComparisonCategoryKey } from "@repo/shared/api";
import type { RoleMismatch, SectionTier } from "@repo/shared/hooks";
import { Trans, useTranslation } from "@repo/shared/i18n";
import {
  comparePlayerKey,
  comparePlayerSlotsSelector,
} from "@repo/shared/redux";
import { Chemistry, DetailedPlayer, Player } from "@repo/shared/types";
import { Plus } from "lucide-react";
import { useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { AddPlayerSearch } from "../AddPlayerSearch";
import { ColumnDividers } from "../ColumnDividers";
import { ComparePlayerCard } from "../ComparePlayerCard";
import { CompareRoleNudge } from "../CompareRoleNudge";
import { CompareSections, useOpenSections } from "../CompareSections";
import {
  CompareStickyPlayersRow,
  useCompareStickyHeader,
} from "../CompareStickyPlayer";
import { ModeToggle, useCompareMode } from "../ModeToggle";
import {
  CARD_BASE_WIDTH,
  CARD_COLUMN_GUTTER,
  MIN_COLUMN_WIDTH,
  columnGridStyle,
} from "../compareLayout";
import { useCompareData } from "../useCompareData";

/** The "add player" button every compare host shares: a "+ Add" pill. */
export const AddPlayerButton = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation("compare");
  return (
    <Button
      variant="primary"
      size="small"
      icon={<Plus />}
      onClick={onClick}
      aria-label={t("add.ariaLabel")}
    >
      {t("add.button")}
    </Button>
  );
};

/**
 * Bottom sheet positioned absolutely within the mobile screen (not the
 * browser viewport), with an in-frame backdrop — the phone-shell equivalent
 * of the design-system BottomSheetView.
 */
const InFrameSheet = ({
  open,
  onClose,
  heightClassName,
  children,
}: {
  open: boolean;
  onClose: () => void;
  heightClassName?: string;
  children: React.ReactNode;
}) => {
  if (!open) return null;
  return (
    <div className="absolute inset-0 z-40 flex items-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div
        className={cn(
          "relative flex w-full flex-col rounded-t-xl bg-panel-middleground py-2 shadow-soft-dark-bottom",
          "transition-all duration-200 translate-y-0 starting:translate-y-full",
          heightClassName ?? "max-h-[85%]",
        )}
      >
        <div className="no-scrollbar min-h-0 w-full grow overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
};

/**
 * The mobile comparison itself — the desktop comparison (same data, cards,
 * sections and behaviors) on a horizontally scrollable column system. Used by
 * the touch-device ComparePage (routed and inside the compare bottom sheet)
 * and by the /compare/mobile phone-shell preview.
 *
 * Column sizing — perView = max(2, floor(width / 170)), visible =
 * min(players, perView), colWidth = max(170, width / visible) — with the
 * container width measured live so it adapts to any real device. The card
 * row and every section live in one shared horizontal scroller (fixed
 * colWidth grid tracks, dividers at k × colWidth, the design-system
 * ScrollBar); the toolbar, section titles and category headers pin to the
 * left edge while the columns scroll underneath. No trailing add column —
 * two full columns always fit the viewport; adding happens from a "+" in the
 * host's header.
 *
 * Scrolls the x axis only. Vertical scrolling stays native with the host
 * (page body / the sheet's content wrapper), so global flow padding like the
 * ad reserve lands after the content instead of cutting the view.
 */
export const MobileComparison = ({
  players,
  sectionTiers,
  categoryWinners,
  metaRatingWinners,
  removePlayer,
  setSlotChemistry,
  setSlotChemStyleId,
  gradedRoleId,
  roleTrigger,
  roleMismatch,
  onSelectRole,
  className,
  subsection,
  stickyTop,
}: {
  players: DetailedPlayer[];
  /** Per-player tier + winner for an endpoint category (see useCompareData). */
  sectionTiers: (key: ComparisonCategoryKey) => SectionTier[];
  /** Winning column indexes for an endpoint category (see useCompareData). */
  categoryWinners: (key: ComparisonCategoryKey) => Set<number>;
  /** Which players hold the best meta rating, in column order. */
  metaRatingWinners: boolean[];
  removePlayer: (player: Player) => void;
  /** Slot chemistry/chemstyle writers (see useCompareData). */
  setSlotChemistry: (player: Player, chemistry: Chemistry) => void;
  setSlotChemStyleId: (player: Player, chemStyleId: number | null) => void;
  /** The selected graded role; each player's own best role when absent. */
  gradedRoleId?: number;
  /** The role-picker trigger, rendered on the toolbar's right. */
  roleTrigger?: React.ReactNode;
  /** Same-role nudge data; rendered pinned between the cards and sections. */
  roleMismatch?: RoleMismatch<PlayerRole>;
  /** Selects the nudge's suggested role (see useCompareData's setPlayerRole). */
  onSelectRole: (role: PlayerRole) => void;
  className?: string;
  /** Which host this screen is in — stamped on the mode-change event. */
  subsection: CompareSubsection;
  /**
   * Where the compressed sticky header pins once the cards scroll out.
   * Defaults to the top of the nearest scroll container (the sheet / phone
   * shell); the routed page passes the navbar offset + viewport mode instead.
   */
  stickyTop?: {
    topClassName: string;
    offset: number;
    withinScrollContainer: boolean;
  };
}) => {
  // Saved chemistry/chemstyleId per column. Straight off the slice (not a
  // prop): both hosts' `players` mirror the same slots in the same order.
  const slots = useSelector(comparePlayerSlotsSelector);
  const [mode, handleModeChange] = useCompareMode({
    subsection,
    playerCount: players.length,
  });
  const { openSections, toggleSection } = useOpenSections();

  // Live width of the container — drives the column math and the
  // pinned-header widths. Fractional (getBoundingClientRect, not clientWidth):
  // integer rounding would leave the columns a px short of the viewport and
  // the viewport-wide pinned rows would overhang them into a tiny x-scroll.
  const rootRef = useRef<HTMLDivElement>(null);
  const [viewportWidth, setViewportWidth] = useState(0);
  useLayoutEffect(() => {
    const node = rootRef.current;
    if (!node) return;
    const measure = () => setViewportWidth(node.getBoundingClientRect().width);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const perView = Math.max(2, Math.floor(viewportWidth / MIN_COLUMN_WIDTH));
  const visible = Math.max(1, Math.min(players.length, perView));
  // Exact division (grid tracks take fractional px): when every column is in
  // view they fill the width edge to edge — no sub-pixel overflow, no slack.
  const colWidth = Math.max(MIN_COLUMN_WIDTH, viewportWidth / visible);
  const totalWidth = players.length * colWidth;
  const cardScale = Math.min(
    1,
    (colWidth - CARD_COLUMN_GUTTER) / CARD_BASE_WIDTH,
  );
  const pinnedWidth = viewportWidth || undefined;

  const gridStyle = columnGridStyle(players.length, colWidth);

  // Sticky compressed header: like desktop, the toolbar pins and swaps its
  // controls for the compressed players once the cards scroll out (sentinel
  // after the card row). Vertical scrolling lives with the host, so the
  // routed page measures against the viewport (under the navbar) and the
  // sheet / phone shell against their own scroll containers.
  const {
    topClassName: stickyTopClassName = "top-0",
    offset: stickyOffset = 0,
    withinScrollContainer = true,
  } = stickyTop ?? {};
  const { scrolledPast, sentinelRef, stickyClassName } = useCompareStickyHeader(
    {
      offset: stickyOffset,
      withinScrollContainer,
      topClassName: stickyTopClassName,
    },
  );

  // The stuck bar sits outside the horizontal scroller, so it mirrors the
  // viewport's scrollLeft with a transform to keep each compressed player
  // over its column. Ref-mutated (no state) — this runs every scroll frame.
  const stickyRowRef = useRef<HTMLDivElement>(null);
  const scrollLeftRef = useRef(0);
  const syncStickyRow = (event: React.UIEvent) => {
    const target = event.target as HTMLElement;
    if (!("radixScrollAreaViewport" in target.dataset)) return;
    scrollLeftRef.current = target.scrollLeft;
    if (stickyRowRef.current)
      stickyRowRef.current.style.transform = `translateX(${-target.scrollLeft}px)`;
  };

  return (
    <div
      ref={rootRef}
      className={cn("relative w-full", className)}
      onScrollCapture={syncStickyRow}
    >
      <div
        className={cn("bg-neutral-800", stickyClassName)}
        style={pinnedWidth != null ? { width: pinnedWidth } : undefined}
      >
        {/* Both states are h-12: a height change on the flip would shift the
            sentinel under the trigger threshold and oscillate the bar. */}
        {scrolledPast ? (
          // Clipped to the viewport width — the translated grid spans every
          // column, including the ones scrolled off-screen.
          <div className="h-12 overflow-hidden">
            <CompareStickyPlayersRow
              rowRef={stickyRowRef}
              players={players}
              gridStyle={gridStyle}
              gradedRoleId={gradedRoleId}
              hideRatings
              className="h-full"
              style={{
                width: totalWidth,
                transform: `translateX(${-scrollLeftRef.current}px)`,
              }}
            />
          </div>
        ) : (
          <div className="flex h-12 items-center justify-between px-3">
            <ModeToggle mode={mode} onChange={handleModeChange} />
            {roleTrigger}
          </div>
        )}
      </div>
      {players.length > 0 ? (
        <ScrollArea type="always" className="w-full">
          <div className="relative pb-8" style={{ width: totalWidth }}>
            {/* Player cards on the shared column axis */}
            <div className="relative pb-3 pt-3">
              <div className="grid" style={gridStyle}>
                {players.map((player, idx) => (
                  <ComparePlayerCard
                    key={comparePlayerKey(player)}
                    player={player}
                    slot={slots[idx]}
                    variant="mobile"
                    scale={cardScale}
                    isMetaRatingWinner={metaRatingWinners[idx]}
                    gradedRoleId={gradedRoleId}
                    onRemove={removePlayer}
                    setSlotChemistry={setSlotChemistry}
                    setSlotChemStyleId={setSlotChemStyleId}
                  />
                ))}
              </div>
              <ColumnDividers count={players.length} />
            </div>

            {/* Sentinel: flips the toolbar into the sticky compressed header
                once the cards scroll out (bottom passes the sticky top). */}
            <div ref={sentinelRef} className="h-0" />

            {/* Same-role nudge — pinned to the left edge like the section
                titles (sticky + inline width, no w-full: Tailwind runs with
                important:true and it would beat the inline pinned width). */}
            {roleMismatch && (
              <div
                className="sticky left-0 px-3 pb-3"
                style={pinnedWidth != null ? { width: pinnedWidth } : undefined}
              >
                <CompareRoleNudge
                  roleMismatch={roleMismatch}
                  onSelectRole={onSelectRole}
                />
              </div>
            )}

            <CompareSections
              players={players}
              gridStyle={gridStyle}
              sectionTiers={sectionTiers}
              categoryWinners={categoryWinners}
              mode={mode}
              openSections={openSections}
              toggleSection={toggleSection}
              pinnedWidth={pinnedWidth}
            />
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      ) : (
        <div className="flex items-center justify-center px-8 py-16 text-center">
          <p className="text-sm text-neutral-500">
            <Trans
              ns="compare"
              i18nKey="empty"
              components={{
                icon: <Plus className="inline size-4 align-text-bottom" />,
              }}
            />
          </p>
        </div>
      )}
    </div>
  );
};

/**
 * Standalone mobile Compare Players screen (used by the /compare/mobile
 * phone-shell preview): in-screen header (back + title + "+"), the
 * comparison, and in-frame bottom sheets for the player search and the role
 * picker.
 */
export const CompareMobile = () => {
  // Opened from any page, so it has no route parent; shared comparisons land
  // on the players list.
  const goBack = useGoBack({ fallback: "/players" });
  const { t } = useTranslation("compare");
  const {
    players,
    sectionTiers,
    categoryWinners,
    metaRatingWinners,
    playerRole,
    setPlayerRole,
    playerRoleLabel,
    playerRoles,
    roleGroups,
    roleMismatch,
    removePlayer,
    setSlotChemistry,
    setSlotChemStyleId,
  } = useCompareData({ syncUrl: true });

  const [searching, setSearching] = useState(false);
  const [rolePickerOpen, setRolePickerOpen] = useState(false);
  // The comparison scrolls inside this wrapper (the window never scrolls
  // here), so "back to top" after adding a player targets it directly.
  const comparisonScrollRef = useRef<HTMLDivElement>(null);

  const pickRole = (role?: RoleOption) => {
    setPlayerRole(
      role ? playerRoles.find((pr) => pr.id === role.id) : undefined,
    );
    setRolePickerOpen(false);
  };

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden bg-neutral-900">
      {/* Header: back + title left, "+" (opens the player search) right. */}
      <header className="flex items-center justify-between gap-x-2 px-3 py-2.5">
        <div className="flex min-w-0 items-center gap-x-3">
          <BackButton onClick={goBack} />
          <h1 className="truncate text-base font-semibold text-neutral-100">
            {t("title")}
          </h1>
        </div>
        <AddPlayerButton onClick={() => setSearching(true)} />
      </header>

      {/* Native vertical scroll (hidden scrollbar); the comparison itself
          only scrolls the x axis. */}
      <div
        ref={comparisonScrollRef}
        className="no-scrollbar min-h-0 grow overflow-y-auto"
      >
        <MobileComparison
          players={players}
          sectionTiers={sectionTiers}
          categoryWinners={categoryWinners}
          metaRatingWinners={metaRatingWinners}
          removePlayer={removePlayer}
          setSlotChemistry={setSlotChemistry}
          setSlotChemStyleId={setSlotChemStyleId}
          gradedRoleId={playerRole?.id}
          roleMismatch={roleMismatch}
          onSelectRole={setPlayerRole}
          roleTrigger={
            <Button
              variant="secondary"
              size="small"
              onClick={() => setRolePickerOpen(true)}
            >
              {playerRole ? playerRoleLabel : t("role.selectRole")}
            </Button>
          }
          subsection="Page"
        />
      </div>

      {/* Player search — in-frame bottom sheet */}
      <InFrameSheet
        open={searching}
        onClose={() => setSearching(false)}
        heightClassName="h-[85%]"
      >
        <AddPlayerSearch
          onAdded={() => {
            setSearching(false);
            comparisonScrollRef.current?.scrollTo({ top: 0 });
          }}
          onCancel={() => setSearching(false)}
          playerRoleId={playerRole?.id}
          subsection="Page"
        />
      </InFrameSheet>

      {/* Role picker — in-frame bottom sheet */}
      <InFrameSheet
        open={rolePickerOpen}
        onClose={() => setRolePickerOpen(false)}
      >
        <RoleSelectSheetContent
          groups={roleGroups}
          selectedRoleId={playerRole?.id}
          onPick={pickRole}
          onClose={() => setRolePickerOpen(false)}
          subtitle={
            <Trans
              ns="compare"
              i18nKey="role.subtitle"
              components={{ hl: <span className="text-neutral-300" /> }}
            />
          }
        />
      </InFrameSheet>
    </div>
  );
};
